import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { SharedDocumentViewer, DocumentPreview, DocumentActionPage } from './features/document-sharing';
import { Calendar, CheckSquare, FileText, LogOut, Plus, Settings, Clipboard } from 'lucide-react';
import { Button } from './components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './components/ui/card';
import { Progress } from './components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { SidebarNavigation } from './components/sidebar-navigation';
import { MobileNavigation } from './components/mobile-navigation';
import { ProjectSelector, ProjectProvider } from './components/ProjectSelector';
import { NewProjectButton } from './components/NewProjectButton';
import { UserProvider, useUser } from './contexts/UserContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import { NavigationProvider, useNavigation } from './contexts/NavigationContext';
import { NavigationLoadError } from './components/NavigationUnavailable';
import { RoutePermissionGuard } from './components/RoutePermissionGuard';
import { useExperience } from './hooks/useExperience';
import { showsInternalChrome } from './lib/experience';
import { TimeTrackerProvider } from './features/project-management/time-tracking/time-tracker-context';
import { colors } from './design-system/colors';

// Import pages
import { Dashboard } from './components/dashboard';
import { CommandCenterPage } from './features/command-center/page';
import { TodosMockupPage } from './features/todos-mockup/page';
import { LeadsPage } from './features/leads/page';
import { EmailMarketingPage } from './features/leads/email-marketing/page';
import { LeadManagementPage } from './features/leads/lead-management/page';
import ProposalsPage from '@/features/leads/proposals/page';
import { ProposalTemplatesPage } from './features/leads/proposal-templates/page';
import { ProjectPage } from './features/project/page';
import { ProjectManagementPage } from './features/project-management/page';
import { DailyLogsPage } from './features/project-management/daily-logs/page';
import { TodosPage } from './features/project-management/todos/page';
import { ChecklistsPage } from './features/project-management/checklists/page';
import MaterialOrdersPage from './features/project-management/material-orders/page';
import SchedulePage from './features/project-management/schedule/page';
import { ChangeOrdersPage } from './features/project-management/change-orders/page';
import { PMSelectionsPage } from './features/project-management/selections/page';
import { WarrantiesPage } from './features/project-management/warranties/page';
import { TimeTrackingPage } from './features/project-management/time-tracking/page';
import SpecificationsPage from './features/project-management/specifications/page';
import { MessagesPage } from './features/messages/page';
import { MessagesListPage } from './features/messages/messages/page';
import { CommentsPage } from './features/messages/comments/page';
import { SurveysPage } from './features/messages/surveys/page';
import { MessageSelectionsPage } from './features/messages/selections/page';
import { DrivePage } from './features/drive/page';
import { CostManagementPage } from './features/cost-management/page';
import { BidsPage } from './features/cost-management/bids/page';
import { BillsPage } from './features/cost-management/bills/page';
import { PurchaseOrdersPage } from './features/cost-management/purchase-orders/page';
import { POPaymentsPage } from './features/cost-management/po-payments/page';
import { BudgetsPage } from './features/cost-management/budgets/page';
import { CatalogPage } from './features/cost-management/catalog/page';
import { OwnerInvoicesPage } from './features/cost-management/owner-invoices/page';
import { PaymentsPage } from './features/cost-management/payments/page';
import { InvoicesPage } from './features/cost-management/invoices/page';
import { InsurancePage } from './features/cost-management/insurance/page';
import { TakeoffPage } from './features/cost-management/takeoff/page';
import { BusinessInsightsPage } from './features/cost-management/business-insights/page';
import { PeoplePage } from './features/people/page';
import { TeamPage } from './features/people/team/page';
import { ClientsPage } from './features/people/clients/page';
import { VendorsPage } from './features/people/vendors/page';
import { ReportsPage } from './features/reports/page';
import { AIChatPage } from './features/ai-chat/page';
import { AIChatWidget, AIChatFloatingButton } from './components/AIChatWidget';
import { ProjectSetupModal } from './components/ProjectSetupModal';
import { NotificationsButton } from './components/notifications/NotificationsButton';
import { NotificationsPage } from './components/notifications/NotificationsPage';
import { SettingsPage } from './features/settings/page';
import { LoginPage } from './components/LoginPage';
import { JoinPage } from './components/JoinPage';
import { readPendingInvitation } from './lib/commercial-invitation-token';
import { ExternalAccountHome } from './components/ExternalAccountHome';
import { AcceptInvitePage } from './components/AcceptInvitePage';
import { SignupPage } from './components/SignupPage';
import { SignupSuccessPage } from './components/SignupSuccessPage';
import { OnboardingWizard } from './components/OnboardingWizard';
import { supabase } from './lib/supabase';
import { waitForInitialSession, verifySessionIsLive, signOutLocally } from './lib/auth-session';
import { useSession } from './hooks/useSession';
import { DriveDocumentViewerPage, AttachmentViewerPage } from './components/DocumentViewerPage';
import { parseDocumentLocation } from './lib/file-urls';
import { StandardButton } from './styles/StandardButtons';
import { posthog } from './lib/posthog';
import { OnboardingProvider } from './features/walkthrough';

function UnauthenticatedLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen w-full">
      {children}
    </div>
  );
}

function AuthenticatedLayout({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  // Root-cause fix (2026-09-20): reads the app-wide session cache
  // (lib/auth-session.ts) reactively instead of keeping its own local
  // `session` state fed by its own onAuthStateChange subscription -- this
  // used to be one of THREE independent subscriptions across the app
  // (App.tsx/UserContext.tsx/LoginPage.tsx), each an extra path into
  // GoTrueClient's shared auth lock. See auth-session.ts's own docs for
  // the full mechanism.
  const session = useSession();
  // True once the boot-time "is this session still good server-side"
  // check has settled one way or another (valid, confirmed invalid --
  // already redirected to /login by then --, or timed out and failing
  // open). Distinct from `session` itself, which can already be non-null
  // the instant the cached token is read, before that confirmation
  // finishes.
  const [authVerified, setAuthVerified] = useState(false);
  const [isAIChatOpen, setIsAIChatOpen] = useState(false);
  const [isProjectSetupOpen, setIsProjectSetupOpen] = useState(false);
  const { companyId, isLoading: isUserLoading, companyAddressMissing, companyFetchError, refreshUser, userRole, accountType, accountTypeResolved } = useUser();
  // A free client / vendor account that no company has added yet has no workspace: it is not a signup that stopped halfway, so it never goes to the company onboarding.
  const isCompanylessExternal = !companyId && (accountType === 'client_account' || accountType === 'vendor_account');
  // the Ask Bob button, the AI chat, project setup and New Project belong to the internal experience only
  const internalChrome = showsInternalChrome(useExperience());
  // Ask Bob and New Project are not offered to a vendor (the role in the current company; the server refuses both as well)
  const isVendor = userRole === 'vendor';
  // the menus, tabs and routes the person's role may open in this company (GET /api/me/navigation): nothing protected is rendered until it has loaded
  const { status: navigationStatus, retry: retryNavigation } = useNavigation();
  // "Ask Bob" floating-button dismissal: lasts only for the current page --
  // resets the moment the route changes, so it never permanently disappears.
  const [isAskBobDismissed, setIsAskBobDismissed] = useState(false);
  useEffect(() => {
    setIsAskBobDismissed(false);
  }, [location.pathname]);

  // ONE-TIME gate, not a per-navigation check: the moment a session is
  // first confirmed on this page load (covers both an explicit login,
  // which reloads the whole app, and reopening/reloading a tab that
  // already had a session), decide once whether this user needs
  // onboarding (no company yet) or needs to complete their company's
  // address (Settings > Company) before going anywhere else. Reuses
  // UserContext's own single fetch (companyId/companyAddressMissing) --
  // no extra query. hasCheckedGateRef makes sure this never re-fires on
  // later in-app navigation, only once per mount.
  const hasCheckedGateRef = useRef(false);
  useEffect(() => {
    if (hasCheckedGateRef.current) return;
    if (!authVerified || !session || isUserLoading) return;
    if (!companyId && !accountTypeResolved) return; // decide only once we know whether this is a free external account
    hasCheckedGateRef.current = true;
    if (!companyId) {
      // someone who signed up from a commercial invitation and just confirmed their email continues with that invitation, not with creating a company
      const pendingInvitation = readPendingInvitation();
      if (pendingInvitation) navigate(`/auth/join?token=${encodeURIComponent(pendingInvitation)}`, { replace: true });
      else if (!isCompanylessExternal) navigate('/onboarding', { replace: true });
    } else if (companyAddressMissing && userRole !== 'client' && location.pathname !== '/settings') { // a client cannot fill in the company's address
      navigate('/settings', { replace: true });
    }
  }, [authVerified, session, isUserLoading, companyId, companyAddressMissing, userRole, location.pathname, navigate, accountTypeResolved, isCompanylessExternal]);

  // Runs once per mount: confirms the cached session is still good
  // server-side (getSession() alone only proves a token is cached
  // locally, not that the account still exists), then flips
  // `authVerified`. A LATER real sign-out is handled by the separate
  // effect below, which just reacts to `session` going null.
  const hasVerifiedRef = useRef(false);
  useEffect(() => {
    if (hasVerifiedRef.current) return;
    hasVerifiedRef.current = true;
    (async () => {
      const initialSession = await waitForInitialSession();
      if (!initialSession) {
        navigate('/login');
        return;
      }

      // That round-trip used to have no timeout (found live 2026-09-20): a
      // slow or unresponsive Auth endpoint left the app stuck on
      // "Loading..." forever, on every refresh and right after login.
      // verifySessionIsLive() fails open on timeout -- trust the cached
      // session and let the user in; a truly dead account still gets
      // caught the moment any real API call is made, same as before this
      // gate existed at all.
      const liveness = await verifySessionIsLive();
      if (liveness === 'invalid') {
        await supabase.auth.signOut();
        navigate('/login');
        return;
      }
      // 'valid' or 'timeout' (fail open) both let the user in.
      setAuthVerified(true);
    })();
  }, [navigate]);

  // Once verified, react to a REAL later sign-out (a SIGNED_OUT event
  // flowing through lib/auth-session.ts, e.g. logging out in another tab,
  // or authenticated-fetch.ts's 401 handler calling signOut()).
  useEffect(() => {
    if (authVerified && !session) {
      navigate('/login');
    }
  }, [authVerified, session, navigate]);

  if (!authVerified) {
    return <div>Loading...</div>;
  }

  if (!session) {
    return null;
  }

  if (!isUserLoading && isCompanylessExternal) {
    return <ExternalAccountHome accountType={accountType as 'client_account' | 'vendor_account'} />;
  }

  if (isUserLoading || !companyId) {
    // Root-cause fix (2026-09-20): fetchUserCompanyRelation() used to have
    // no timeout at all, so a slow/hung query left this branch -- and the
    // whole app shell -- stuck on "Loading..." forever. It now times out
    // (UserContext.tsx) and, when it can't recover from a cached fallback
    // either, sets companyFetchError -- show that explicitly with a Retry
    // instead of spinning indefinitely.
    if (companyFetchError) {
      return (
        <div className="flex min-h-screen w-full items-center justify-center">
          <div className="max-w-sm text-center">
            <p className="mb-4 text-sm text-gray-600">{companyFetchError}</p>
            <StandardButton customVariant="blue" onClick={() => refreshUser()}>
              Retry
            </StandardButton>
          </div>
        </div>
      );
    }
    // Either still resolving membership, or the redirect effect above is
    // about to send this user to /onboarding -- don't flash the full app
    // shell (sidebar, project selector) at someone with no company yet.
    return <div>Loading...</div>;
  }

  // No fallback: without the navigation of this company + role there is no sidebar and no page, only a clear error and a retry
  if (navigationStatus === 'error') return <NavigationLoadError onRetry={retryNavigation} />;
  if (navigationStatus !== 'loaded') return <div>Loading...</div>;

  return (
    <div className="flex h-screen w-full flex-col overflow-hidden">
      <div className="flex-shrink-0">
        {/* md+: 3-column grid (1fr auto 1fr) so the Project Selector's center is
            the header's own center, independent of how wide the left/right
            groups are. Below md: the original wrapping flex row. */}
        <div
          className="flex flex-wrap items-center gap-3 px-4 md:px-6 py-3 md:grid md:grid-cols-[1fr_auto_1fr]"
          style={{ 
            width: "100%",
            backgroundColor: colors.header
          }}
        >
          <div className="flex items-center gap-3 flex-shrink-0">
            <MobileNavigation onNavigate={navigate} onAIChatOpen={() => setIsProjectSetupOpen(true)} />
            <div
              className="flex items-center gap-2 font-semibold flex-shrink-0 cursor-pointer"
              style={{ color: colors.buttonTextWhite }}
              onClick={() => navigate('/')}
            >
              <img
                src="/KrewUp - Logo4.png"
                alt="KrewUp Logo"
                className="h-7 w-auto"
              />
            </div>
          </div>
          <div className="flex-1 min-w-[200px] flex justify-center md:flex-none md:min-w-0">
            <ProjectSelector />
          </div>
          <div className="flex items-center gap-4 md:gap-6 flex-shrink-0 ml-auto md:ml-0 md:justify-self-end">
            <NotificationsButton />
            <Button 
              variant="outline" 
              size="sm"
              style={{ 
                backgroundColor: colors.buttonSecondary,
                color: colors.buttonTextWhite,
                border: 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = colors.buttonSecondaryHover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = colors.buttonSecondary;
              }}
              onClick={async () => {
                posthog.capture('user_logout');
                await signOutLocally();
                navigate('/login');
              }}
            >
              Logout
            </Button>
            {internalChrome && !isVendor && <NewProjectButton />}
          </div>
        </div>
      </div>
      <div className="flex flex-1 min-h-0">
        <SidebarNavigation onNavigate={navigate} onAIChatOpen={() => setIsProjectSetupOpen(true)} />
        <main className="flex flex-1 flex-col min-w-0 min-h-0 overflow-y-auto">
          {children}
        </main>
      </div>
      {internalChrome && !isVendor && <AIChatWidget isOpen={isAIChatOpen} onClose={() => setIsAIChatOpen(false)} />}
      {internalChrome && !isVendor && <ProjectSetupModal isOpen={isProjectSetupOpen} onClose={() => setIsProjectSetupOpen(false)} />}
      {internalChrome && !isVendor && !isAIChatOpen && !isAskBobDismissed && (
        <AIChatFloatingButton onClick={() => setIsAIChatOpen(true)} onDismiss={() => setIsAskBobDismissed(true)} />
      )}
    </div>
  );
}

function AppContent() {
  const location = useLocation();
  const isUnauthenticatedRoute = ['/setup', '/login', '/auth/login', '/auth/signup', '/auth/signup/success', '/accept-invite', '/auth/join'].includes(location.pathname);
  // Public shared-document viewer: opened by external recipients (e.g. a
  // subcontractor) who have no KrewUp session, so it must bypass the auth gate
  // and the app chrome entirely.
  // /a/:token = emailed Approve/Reject link (DocumentActionPage) -- same
  // public, no-session posture as /d/:token.
  const isSharedDocRoute = location.pathname.startsWith('/d/') || location.pathname.startsWith('/a/');

  // Track page views with PostHog
  useEffect(() => {
    if (!isUnauthenticatedRoute && !isSharedDocRoute) {
      posthog.capture('$pageview', {
        page: location.pathname,
        page_title: document.title
      });
    }
  }, [location.pathname, isUnauthenticatedRoute, isSharedDocRoute]);

  if (isSharedDocRoute) {
    return (
      <Routes>
        <Route path="/d/:token" element={<SharedDocumentViewer />} />
        <Route path="/a/:token" element={<DocumentActionPage />} />
      </Routes>
    );
  }

  // The permanent page of a document (/drive/document/:id, /attachments/:id): it needs the person's own session but renders OUTSIDE the app shell -- it is
  // the file itself, full page. DocumentViewerPage sends a signed-out visitor to Login (which returns here) and shows "Access denied" when the backend refuses.
  if (parseDocumentLocation(location.pathname)) {
    return (
      <Routes>
        <Route path="/drive/document/:id" element={<DriveDocumentViewerPage />} />
        <Route path="/attachments/:id" element={<AttachmentViewerPage />} />
      </Routes>
    );
  }

  // Send/Export separation (2026-09-14): "Export PDF" needs the caller's
  // OWN authenticated session (unlike /d/:token, which is public) but must
  // render OUTSIDE AuthenticatedLayout's app shell -- the sidebar/topbar
  // chrome would otherwise show up in the printed/exported PDF. Same
  // "skip the shell, not the auth gate" pattern as /onboarding above:
  // DocumentPreview relies on the existing authenticated-fetch interceptor
  // to attach the session automatically and shows its own error state if
  // the backend 401s.
  if (location.pathname.startsWith('/documents/') && location.pathname.endsWith('/preview')) {
    return (
      <Routes>
        <Route path="/documents/:type/:id/preview" element={<DocumentPreview />} />
      </Routes>
    );
  }

  // The onboarding wizard needs an authenticated session (it reads the
  // caller's own email/JWT) but must render OUTSIDE AuthenticatedLayout's
  // app shell -- that shell assumes a real companyId, which is exactly
  // what this page exists to create. OnboardingWizard does its own
  // session check and redirects to /login if there isn't one.
  if (location.pathname === '/onboarding') {
    return (
      <UnauthenticatedLayout>
        <Routes>
          <Route path="/onboarding" element={<OnboardingWizard />} />
        </Routes>
      </UnauthenticatedLayout>
    );
  }

  if (isUnauthenticatedRoute) {
    return (
      <UnauthenticatedLayout>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/login" element={<LoginPage />} />
          <Route path="/auth/signup" element={<SignupPage />} />
          <Route path="/auth/signup/success" element={<SignupSuccessPage />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          <Route path="/auth/join" element={<JoinPage />} />
        </Routes>
      </UnauthenticatedLayout>
    );
  }

  return (
    <AuthenticatedLayout>
      <OnboardingProvider>
      <RoutePermissionGuard>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/command-center" element={<CommandCenterPage />} />
        <Route path="/todos-mockup" element={<TodosMockupPage />} />
        <Route path="/leads" element={<LeadsPage />} />
        <Route path="/leads/email-marketing" element={<EmailMarketingPage />} />
        <Route path="/leads/lead-management" element={<LeadManagementPage />} />
        <Route path="/leads/proposals" element={<ProposalsPage />} />
        <Route path="/leads/proposal-templates" element={<ProposalTemplatesPage />} />
        <Route path="/project" element={<ProjectPage />} />
        <Route path="/project-management" element={<ProjectManagementPage />} />
        <Route path="/project-management/daily-logs" element={<DailyLogsPage />} />
        <Route path="/project-management/todos" element={<TodosPage />} />
        <Route path="/project-management/checklists" element={<ChecklistsPage />} />
        <Route path="/project-management/material-orders" element={<MaterialOrdersPage />} />
        <Route path="/project-management/schedule" element={<SchedulePage />} />
        <Route path="/project-management/change-orders" element={<ChangeOrdersPage />} />
        <Route path="/project-management/selections" element={<PMSelectionsPage />} />
        <Route path="/project-management/warranties" element={<WarrantiesPage />} />
        <Route path="/project-management/time-tracking" element={<TimeTrackingPage />} />
        <Route path="/project-management/specifications" element={<SpecificationsPage />} />
        <Route path="/messages" element={<MessagesPage />} />
        <Route path="/messages/messages" element={<MessagesListPage />} />
        <Route path="/messages/comments" element={<CommentsPage />} />
        <Route path="/messages/surveys" element={<SurveysPage />} />
        <Route path="/messages/selections" element={<MessageSelectionsPage />} />
        <Route path="/drive" element={<DrivePage />} />
        <Route path="/cost-management" element={<CostManagementPage />} />
        <Route path="/cost-management/bids" element={<BidsPage />} />
        <Route path="/cost-management/bills" element={<BillsPage />} />
        <Route path="/cost-management/purchase-orders" element={<PurchaseOrdersPage />} />
        <Route path="/cost-management/po-payments" element={<POPaymentsPage />} />
        <Route path="/cost-management/budgets" element={<BudgetsPage />} />
        <Route path="/cost-management/catalog" element={<CatalogPage />} />
        <Route path="/cost-management/owner-invoices" element={<OwnerInvoicesPage />} />
        <Route path="/cost-management/payments" element={<PaymentsPage />} />
        <Route path="/cost-management/invoices" element={<InvoicesPage />} />
        <Route path="/cost-management/insurance" element={<InsurancePage />} />
        <Route path="/cost-management/takeoff" element={<TakeoffPage />} />
        <Route path="/cost-management/business-insights" element={<BusinessInsightsPage />} />
        <Route path="/people" element={<PeoplePage />} />
        <Route path="/people/team" element={<TeamPage />} />
        <Route path="/people/clients" element={<ClientsPage />} />
        <Route path="/people/vendors" element={<VendorsPage />} />
        <Route path="/catalog" element={<Navigate to="/cost-management/catalog" replace />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/ai-chat" element={<AIChatPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Routes>
      </RoutePermissionGuard>
      </OnboardingProvider>
    </AuthenticatedLayout>
  );
}

function App() {
  return (
    <Router>
      <UserProvider>
        <PermissionsProvider>
          <NavigationProvider>
            <ProjectProvider>
              {/* App-level: the time tracker keeps running across pages and Project Selector changes. */}
              <TimeTrackerProvider>
                <AppContent />
              </TimeTrackerProvider>
            </ProjectProvider>
          </NavigationProvider>
        </PermissionsProvider>
      </UserProvider>
    </Router>
  );
}

export default App;
